// RegionMoveControls — 範囲の近くに出るボタン群と、レイヤーのプルダウン
// (docs/_wip/region-move-2026-10-15.md「UI」)。
//
//   範囲を選んだ直後 : [レイヤー2 ▾] [移動] [コピー]
//   絵が浮いた後     : 置き先: [レイヤー3 ▾] [確定] [やめる]   (1 枚から取ったときだけ置き先)
//
// - 見た目は `variant` で Modern / Classic を切り替える (中身と挙動は同じ)。
// - 選んだ直後の [移動] [コピー] はどちらも強調しない。片方に色が付いていると「もう選ばれて
//   いる / もう動き出している」に見えて迷う (2026-10-17 作者 staging 確認)。強調は浮かせた後の [確定] だけ。
// - ボタンは `useGuardedTap` (pointerup 起動。ゴーストクリックでは起動しない)、プルダウンは
//   `usePopupGhostGuard` (開いたあとに中で始まった pointerdown を伴う click だけ受理)。
// - チェックは input+label、トリガは `aria-expanded`。Esc はまずプルダウンを閉じる。
// - 狭い画面では折り返す (`flex-wrap`)。

import { CLASSIC_LAYER_COUNT } from '@oekaki/protocol';
import { useEffect, useId, useRef, useState } from 'react';

import {
  allLayerIds,
  regionLayerLabel,
  selectAllSourceLayers,
  summarizeSourceLayers,
  toggleSourceLayer,
} from '../../../application/regionMoveFlow.js';
import { useGuardedTap, usePopupGhostGuard } from '../../hooks/useGuardedTap.js';
import type { RegionMoveController } from '../../hooks/useRegionMove.js';
import { legacyToggleButtonClass } from '../classic/legacyFrames.js';

export type RegionVariant = 'modern' | 'classic';

const buttonClass = (
  variant: RegionVariant,
  opts?: { primary?: boolean; disabled?: boolean },
): string => {
  const disabled = opts?.disabled === true;
  if (variant === 'classic') {
    return `flex h-[26px] items-center justify-center whitespace-nowrap px-2 text-[11px] font-bold ${legacyToggleButtonClass(false, disabled)}`;
  }
  const tone =
    opts?.primary === true
      ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-black'
      : 'border-[var(--color-border)] bg-[var(--color-panel)] text-white';
  return `flex h-8 items-center justify-center whitespace-nowrap rounded-md border px-2.5 text-[12px] font-bold ${tone} ${
    disabled ? 'cursor-not-allowed opacity-40' : 'hover:brightness-110'
  }`;
};

const panelClass = (variant: RegionVariant): string =>
  variant === 'classic'
    ? 'border border-[var(--color-border)] bg-white p-1.5 text-black shadow-md'
    : 'rounded-md border border-[var(--color-border)] bg-[var(--color-panel)] p-2 text-white shadow-xl';

/** 押せるボタン (pointerup 起動 + キーボード)。 */
const GuardedButton = ({
  label,
  onActivate,
  disabled = false,
  variant,
  primary = false,
  testId,
  title,
}: {
  readonly label: string;
  readonly onActivate: () => void;
  readonly disabled?: boolean;
  readonly variant: RegionVariant;
  readonly primary?: boolean;
  readonly testId?: string;
  readonly title?: string;
}): React.ReactNode => {
  const tap = useGuardedTap(disabled ? undefined : onActivate);
  return (
    <button
      type="button"
      disabled={disabled}
      data-testid={testId}
      title={title}
      {...tap}
      className={buttonClass(variant, { primary, disabled })}
    >
      {label}
    </button>
  );
};

/**
 * 閉じた表示 `[ラベル ▾]` + 開くと panel。外側の押下 / Esc で閉じる。
 * 上下どちらへ開くかは開いた時点のトリガ位置で決める (画面の下半分なら上へ)。
 */
const Dropdown = ({
  triggerLabel,
  triggerAriaLabel,
  triggerTestId,
  variant,
  disabled = false,
  panelLabel,
  children,
}: {
  readonly triggerLabel: string;
  readonly triggerAriaLabel: string;
  readonly triggerTestId: string;
  readonly variant: RegionVariant;
  readonly disabled?: boolean;
  readonly panelLabel: string;
  readonly children: (
    close: () => void,
    guard: ReturnType<typeof usePopupGhostGuard>,
  ) => React.ReactNode;
}): React.ReactNode => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelId = useId();
  const guard = usePopupGhostGuard(open);

  const toggle = (): void => {
    if (!open) {
      const r = triggerRef.current?.getBoundingClientRect();
      setOpenUp(r !== undefined && r.top > window.innerHeight / 2);
    }
    setOpen((v) => !v);
  };
  const tap = useGuardedTap(disabled ? undefined : toggle);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent): void => {
      if (
        rootRef.current !== null &&
        e.target instanceof Node &&
        rootRef.current.contains(e.target)
      )
        return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: 中のトリガ / チェックから bubble する Esc を拾うだけ
    <div
      ref={rootRef}
      className="relative"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          // まずプルダウンだけ閉じる (範囲の「やめる」まで巻き込まない)。
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
          setOpen(false);
          triggerRef.current?.focus();
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={triggerAriaLabel}
        data-testid={triggerTestId}
        disabled={disabled}
        {...tap}
        className={`${buttonClass(variant, { disabled })} gap-1`}
      >
        <span>{triggerLabel}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {open ? (
        <fieldset
          id={panelId}
          aria-label={panelLabel}
          onPointerDownCapture={guard.onPointerDownCapture}
          className={`absolute left-0 z-10 m-0 min-w-[11rem] ${openUp ? 'bottom-full mb-1' : 'top-full mt-1'} ${panelClass(variant)}`}
        >
          {children(() => setOpen(false), guard)}
        </fieldset>
      ) : null}
    </div>
  );
};

/** 「どこから」: 動かすレイヤーのチェック (複数)。閉じた表示は `レイヤー2` / `レイヤー2・3` / `全部のレイヤー`。 */
export const RegionSourceSelect = ({
  value,
  onChange,
  variant,
  layerCount = CLASSIC_LAYER_COUNT,
}: {
  readonly value: readonly number[];
  readonly onChange: (ids: readonly number[]) => void;
  readonly variant: RegionVariant;
  readonly layerCount?: number;
}): React.ReactNode => {
  const summary = summarizeSourceLayers(value, layerCount);
  const rows = [...allLayerIds(layerCount)].reverse(); // 上が最前面 (レイヤー3 → 1)
  return (
    <Dropdown
      triggerLabel={summary}
      triggerAriaLabel={`動かすレイヤー: ${summary}`}
      triggerTestId="region-source-select"
      variant={variant}
      panelLabel="動かすレイヤー"
    >
      {(_close, guard) => (
        <div className="flex flex-col gap-1">
          <div className="text-[11px] font-bold opacity-70">動かすレイヤー</div>
          {rows.map((id) => (
            <label key={id} className="flex cursor-pointer items-center gap-1.5 text-[12px]">
              <input
                type="checkbox"
                checked={value.includes(id)}
                data-testid={`region-source-layer-${id}`}
                onChange={(e) => {
                  if (!guard.allow((e.nativeEvent as MouseEvent).detail ?? 0)) return;
                  onChange(toggleSourceLayer(value, id));
                }}
              />
              <span>{regionLayerLabel(id)}</span>
            </label>
          ))}
          <SelectAllButton
            variant={variant}
            onActivate={() => onChange(selectAllSourceLayers(layerCount))}
          />
        </div>
      )}
    </Dropdown>
  );
};

const SelectAllButton = ({
  onActivate,
  variant,
}: {
  readonly onActivate: () => void;
  readonly variant: RegionVariant;
}): React.ReactNode => {
  const tap = useGuardedTap(onActivate);
  return (
    <button
      type="button"
      data-testid="region-source-all"
      {...tap}
      className={`${buttonClass(variant)} mt-0.5 h-7 text-[11px]`}
    >
      全部選ぶ
    </button>
  );
};

/** 「どこへ」: 置き先のレイヤー (1 つだけ)。元には「(取った元)」を添える。選んだらすぐ閉じる。 */
export const RegionDstSelect = ({
  value,
  srcLayerId,
  onChange,
  variant,
  layerCount = CLASSIC_LAYER_COUNT,
}: {
  readonly value: number;
  /** 取った元のレイヤー。 */
  readonly srcLayerId: number;
  readonly onChange: (id: number) => void;
  readonly variant: RegionVariant;
  readonly layerCount?: number;
}): React.ReactNode => {
  const rows = [...allLayerIds(layerCount)].reverse();
  const groupName = useId();
  return (
    <Dropdown
      triggerLabel={regionLayerLabel(value)}
      triggerAriaLabel={`置き先: ${regionLayerLabel(value)}`}
      triggerTestId="region-dst-select"
      variant={variant}
      panelLabel="置き先のレイヤー"
    >
      {(close, guard) => (
        <div className="flex flex-col gap-1">
          <div className="text-[11px] font-bold opacity-70">置き先のレイヤー</div>
          {rows.map((id) => (
            <label key={id} className="flex cursor-pointer items-center gap-1.5 text-[12px]">
              <input
                type="radio"
                name={groupName}
                checked={value === id}
                data-testid={`region-dst-layer-${id}`}
                onChange={(e) => {
                  if (!guard.allow((e.nativeEvent as MouseEvent).detail ?? 0)) return;
                  onChange(id);
                  close();
                }}
              />
              <span>
                {regionLayerLabel(id)}
                {id === srcLayerId ? ' (取った元)' : ''}
              </span>
            </label>
          ))}
        </div>
      )}
    </Dropdown>
  );
};

/** 範囲の近くのボタン群。phase が selected / floating のときだけ出る (それ以外は null)。 */
export const RegionActionCluster = ({
  region,
  variant,
  hasOverflow,
  layerCount = CLASSIC_LAYER_COUNT,
}: {
  readonly region: RegionMoveController;
  readonly variant: RegionVariant;
  /** 浮かせている絵がキャンバスの外にはみ出している (確定すると切れる)。 */
  readonly hasOverflow: boolean;
  readonly layerCount?: number;
}): React.ReactNode => {
  const { phase } = region;
  if (phase.kind === 'selected') {
    return (
      <>
        <RegionSourceSelect
          value={region.srcLayerIds}
          onChange={region.setSourceLayers}
          variant={variant}
          layerCount={layerCount}
        />
        <GuardedButton
          label="移動"
          testId="region-move"
          variant={variant}
          disabled={!region.canStart}
          title={
            region.srcLayerIds.length === 0
              ? '動かすレイヤーを選んでください'
              : '範囲を浮かせて動かす'
          }
          onActivate={() => region.start('move')}
        />
        <GuardedButton
          label="コピー"
          testId="region-copy"
          variant={variant}
          disabled={!region.canStart}
          title={
            region.srcLayerIds.length === 0
              ? '動かすレイヤーを選んでください'
              : '範囲を複製して動かす'
          }
          onActivate={() => region.start('copy')}
        />
        <GuardedButton
          label="やめる"
          testId="region-cancel"
          variant={variant}
          title="範囲の選択をやめる (Esc)"
          onActivate={region.cancel}
        />
      </>
    );
  }
  if (phase.kind === 'floating') {
    const single = region.dstLayerId !== null && phase.srcLayerIds.length === 1;
    return (
      <>
        {single && region.dstLayerId !== null ? (
          <div className="flex items-center gap-1">
            <span
              className={`text-[12px] font-bold ${variant === 'classic' ? 'text-black' : 'text-white'}`}
            >
              置き先:
            </span>
            <RegionDstSelect
              value={region.dstLayerId}
              srcLayerId={phase.srcLayerIds[0] as number}
              onChange={region.setDstLayerId}
              variant={variant}
              layerCount={layerCount}
            />
          </div>
        ) : null}
        <GuardedButton
          label={phase.sending ? '送信中…' : '確定'}
          testId="region-confirm"
          variant={variant}
          primary
          disabled={phase.sending}
          title="確定 (Enter)"
          onActivate={region.confirm}
        />
        <GuardedButton
          label="やめる"
          testId="region-cancel"
          variant={variant}
          disabled={phase.sending}
          title="やめる (Esc)"
          onActivate={region.cancel}
        />
        {hasOverflow ? (
          <span
            data-testid="region-overflow-note"
            className={`basis-full text-[11px] ${variant === 'classic' ? 'text-red-700' : 'text-red-300'}`}
          >
            はみ出した部分は確定すると切れます
          </span>
        ) : null}
      </>
    );
  }
  return null;
};
